import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { axiosInstance, API } from "../../utils/api";
import UpgradeHotspot from "../common/UpgradeHotspot";
import "../common/UpgradeHotspot.css";
import { QRCodeSVG } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryReport, setSummaryReport] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summaryType, setSummaryType] = useState('all');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedSurveyId, setEmbedSurveyId] = useState(null);
  const [embedText, setEmbedText] = useState('How did we do?');
  const [showSurveySelector, setShowSurveySelector] = useState(false);
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrSurvey, setQrSurvey] = useState(null);
  const qrRef = useRef(null);
  const fileInputRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    loadUser();
    loadSurveys();
    loadSubscriptionInfo();
  }, []);

  useEffect(() => {
    if (location.pathname === '/dashboard') {
      loadUser();
    }
  }, [location.pathname]);

  const loadUser = async () => {
    try {
      const response = await axiosInstance.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  const loadSurveys = async () => {
    try {
      const response = await axiosInstance.get(`${API}/surveys`);
      setSurveys(response.data);
    } catch (error) {
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionInfo = async () => {
    try {
      const response = await axiosInstance.get(`${API}/subscription/status`);
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to load subscription info:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  const handleDelete = async (surveyId) => {
    const confirmMessage = `⚠️ Delete this survey?\n\nThis will permanently delete:\n• The survey and all its questions\n• ALL responses collected for this survey\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      await axiosInstance.delete(`${API}/surveys/${surveyId}`);
      toast.success('Survey and all responses deleted');
      loadSurveys();
      loadSubscriptionInfo();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleResendVerification = async () => {
    try {
      await axiosInstance.post(`${API}/auth/resend-verification`);
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    }
  };

  const handleDuplicate = (survey) => {
    navigate('/builder', { state: { duplicateSurvey: survey } });
    toast.success('Survey loaded for duplication');
  };

  const handleToggleActive = async (surveyId, currentStatus) => {
    try {
      const response = await axiosInstance.patch(`${API}/surveys/${surveyId}/toggle-active`);
      toast.success(response.data.message);
      loadSurveys();
    } catch (error) {
      toast.error('Failed to toggle survey status');
    }
  };

  const handleExportSurvey = async (surveyId, surveyTitle) => {
    try {
      const response = await axiosInstance.get(`${API}/surveys/${surveyId}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${surveyTitle.replace(/[^a-zA-Z0-9]/g, '_')}_responses.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Survey responses exported!');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No responses to export yet');
      } else {
        toast.error('Failed to export survey');
      }
    }
  };

  const handleEmbedLink = (surveyId) => {
    setEmbedSurveyId(surveyId);
    setEmbedText('How did we do?');
    setShowEmbedModal(true);
  };

  const copyEmbedCode = () => {
    const link = `${window.location.origin}/survey/${embedSurveyId}`;
    const embedCode = `<a href="${link}">${embedText}</a>`;
    
    try {
      const tempTextarea = document.createElement('textarea');
      tempTextarea.value = embedCode;
      tempTextarea.style.position = 'absolute';
      tempTextarea.style.left = '-9999px';
      document.body.appendChild(tempTextarea);
      tempTextarea.select();
      document.execCommand('copy');
      document.body.removeChild(tempTextarea);
      toast.success('HTML code copied!');
    } catch (error) {
      toast.error('Failed to copy. Please try again.');
    }
  };

  const copyForEmail = async () => {
    const link = `${window.location.origin}/survey/${embedSurveyId}`;
    const text = embedText || 'How did we do?';
    
    try {
      const tempElement = document.createElement('div');
      tempElement.contentEditable = 'true';
      tempElement.innerHTML = `<a href="${link}">${text}</a>`;
      tempElement.style.position = 'absolute';
      tempElement.style.left = '-9999px';
      document.body.appendChild(tempElement);
      
      const range = document.createRange();
      range.selectNodeContents(tempElement);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand('copy');
      selection.removeAllRanges();
      document.body.removeChild(tempElement);
      
      toast.success('Link copied! Paste into your email (Ctrl+V or Cmd+V)');
    } catch (error) {
      toast.error('Failed to copy. Please try again.');
    }
  };

  const copyPlainLink = () => {
    const link = `${window.location.origin}/survey/${embedSurveyId}`;
    
    try {
      const tempTextarea = document.createElement('textarea');
      tempTextarea.value = link;
      tempTextarea.style.position = 'absolute';
      tempTextarea.style.left = '-9999px';
      document.body.appendChild(tempTextarea);
      tempTextarea.select();
      document.execCommand('copy');
      document.body.removeChild(tempTextarea);
      toast.success('Survey link copied!');
    } catch (error) {
      toast.error('Failed to copy. Please try again.');
    }
  };

  // QR Code Functions
  const handleShowQR = (survey) => {
    setQrSurvey(survey);
    setShowQRModal(true);
  };

  const downloadQRCode = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, 300, 300);
      
      const link = document.createElement('a');
      link.download = `${qrSurvey.title.replace(/[^a-zA-Z0-9]/g, '_')}_QR.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR code downloaded!');
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Template Export/Import Functions
  const exportTemplate = (survey) => {
    const template = {
      version: "1.0",
      exported_at: new Date().toISOString(),
      template: {
        title: survey.title,
        description: survey.description || "",
        questions: survey.questions.map(q => ({
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options || null,
          required: q.required !== false
        })),
        metadata_collection_mode: survey.metadata_collection_mode || "none",
        allow_ai_assistance: survey.allow_ai_assistance !== false
      }
    };

    const blob = new Blob([JSON.stringify(template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${survey.title.replace(/[^a-zA-Z0-9]/g, '_')}_template.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Template exported!');
  };

  const handleImportTemplate = () => {
    fileInputRef.current?.click();
  };

  const processImportedTemplate = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.template || !data.template.title || !data.template.questions) {
        throw new Error('Invalid template format');
      }

      // Navigate to builder with the imported template
      navigate('/builder', { 
        state: { 
          duplicateSurvey: {
            ...data.template,
            questions: data.template.questions.map((q, i) => ({
              ...q,
              id: `imported_${Date.now()}_${i}`
            }))
          },
          isImport: true 
        } 
      });
      toast.success('Template imported! Customize and save your new survey.');
    } catch (error) {
      toast.error('Invalid template file. Please use a valid JSON template.');
    }
    
    // Reset file input
    event.target.value = '';
  };

  const handleGenerateSummary = async () => {
    if (surveys.length === 0) {
      toast.error('No surveys to summarize');
      return;
    }
    setSelectedSurveys(surveys.map(s => s.id));
    setShowSurveySelector(true);
  };

  const generateSelectedSummary = async () => {
    if (selectedSurveys.length === 0) {
      toast.error('Please select at least one survey');
      return;
    }

    setShowSurveySelector(false);
    setGeneratingSummary(true);
    setShowSummaryModal(true);
    setSummaryReport(null);
    setSummaryType('all');

    try {
      const response = await axiosInstance.post(`${API}/ai/generate-summary-report`, {
        survey_ids: selectedSurveys
      });
      setSummaryReport(response.data);
    } catch (error) {
      toast.error('Failed to generate summary');
      setShowSummaryModal(false);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const toggleSurveySelection = (surveyId) => {
    setSelectedSurveys(prev => 
      prev.includes(surveyId) 
        ? prev.filter(id => id !== surveyId)
        : [...prev, surveyId]
    );
  };

  const selectAllSurveys = () => {
    setSelectedSurveys(surveys.map(s => s.id));
  };

  const deselectAllSurveys = () => {
    setSelectedSurveys([]);
  };

  const handleGenerateSurveySummary = async (surveyId, surveyTitle) => {
    setGeneratingSummary(true);
    setShowSummaryModal(true);
    setSummaryReport(null);
    setSummaryType('single');

    try {
      const response = await axiosInstance.post(`${API}/ai/generate-survey-summary/${surveyId}`);
      setSummaryReport(response.data);
    } catch (error) {
      toast.error('Failed to generate survey summary');
      setShowSummaryModal(false);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const downloadSummaryAsText = () => {
    if (!summaryReport) return;
    
    const reportTitle = summaryType === 'all' 
      ? 'AI Summary Report - All Surveys' 
      : `AI Summary - ${summaryReport.survey_title}`;
    
    let content = `${reportTitle}\n`;
    content += `${'='.repeat(reportTitle.length)}\n\n`;
    content += `Generated: ${new Date(summaryReport.generated_at).toLocaleString()}\n\n`;
    
    if (summaryType === 'all' && summaryReport.total_surveys) {
      content += `Total Surveys: ${summaryReport.total_surveys}\n`;
    }
    content += `Total Responses: ${summaryReport.total_responses}\n\n`;
    
    content += `EXECUTIVE SUMMARY\n`;
    content += `${'-'.repeat(50)}\n`;
    content += `${summaryReport.executive_summary}\n\n`;
    
    content += `OVERALL SENTIMENT\n`;
    content += `${'-'.repeat(50)}\n`;
    content += `${summaryReport.overall_sentiment}\n\n`;
    
    content += `KEY INSIGHTS\n`;
    content += `${'-'.repeat(50)}\n`;
    summaryReport.key_insights.forEach((insight, index) => {
      content += `${index + 1}. ${insight}\n`;
    });
    content += `\n`;
    
    content += `RECOMMENDATIONS\n`;
    content += `${'-'.repeat(50)}\n`;
    summaryReport.recommendations.forEach((rec, index) => {
      content += `${index + 1}. ${rec}\n`;
    });
    
    try {
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = summaryType === 'all' 
        ? `AI_Summary_Report_${new Date().toISOString().split('T')[0]}.txt`
        : `AI_Summary_${summaryReport.survey_title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Summary downloaded! Check your Downloads folder.');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Please try again.');
    }
  };

  const printSummary = () => {
    try {
      const modalContent = document.querySelector('.modal-content');
      if (modalContent) {
        modalContent.classList.add('printing');
      }
      
      setTimeout(() => {
        window.print();
        if (modalContent) {
          modalContent.classList.remove('printing');
        }
      }, 100);
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Print failed. Please try again.');
    }
  };

  if (loading) {
    return <div className="loading-spinner" data-testid="loading-spinner">Loading...</div>;
  }

  return (
    <div className="dashboard-container" data-testid="dashboard">
      <Toaster position="top-right" richColors />
      <div className="dashboard-header">
        <div className="logo" data-testid="logo">
          <span className="brand-cx">CX</span>
          <span className="brand-slash">/</span>
          <span className="brand-recon">recon</span>
          <span className="brand-tagline">: Ground Truth Wins.</span>
        </div>
        <div className="user-section">
          {user && user.is_admin && (
            <button 
              className="admin-access-btn" 
              onClick={() => navigate('/admin')}
              data-testid="admin-access-button"
            >
              🔧 Admin
            </button>
          )}
          {subscriptionInfo && (subscriptionInfo.subscription_status === 'free' || subscriptionInfo.subscription_status === 'recruit') && (
            <button 
              className="upgrade-badge" 
              onClick={() => setShowUpgradeModal(true)}
              data-testid="upgrade-badge"
            >
              🎖️ Recruit ({subscriptionInfo.surveys_remaining} surveys left)
            </button>
          )}
          {subscriptionInfo && subscriptionInfo.subscription_status !== 'free' && subscriptionInfo.subscription_status !== 'recruit' && (
            <button 
              className="pro-badge clickable" 
              onClick={() => navigate('/manage-subscription')}
              data-testid="pro-badge"
              title="Click to manage subscription"
            >
              {
                subscriptionInfo.subscription_status === 'sergeant' || subscriptionInfo.subscription_status === 'monthly' 
                  ? '⭐ Sergeant ⚙️' 
                  : subscriptionInfo.subscription_status === 'lieutenant' || subscriptionInfo.subscription_status === 'yearly'
                  ? '🏅 Lieutenant ⚙️'
                  : 'Pro ⚙️'
              }
            </button>
          )}
          {user && (
            <>
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="user-avatar" data-testid="user-avatar" />
              ) : (
                <div className="user-avatar-placeholder" data-testid="user-avatar-placeholder">
                  {user.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span data-testid="user-name">{user.name}</span>
              <button className="logout-btn" onClick={handleLogout} data-testid="logout-button">
                Logout
              </button>
            </>
          )}
        </div>
      </div>
      
      <div className="dashboard-content">
        {user && !user.email_verified && user.auth_type === 'email' && (
          <div className="verification-banner">
            <div className="verification-content">
              <span className="verification-icon">📧</span>
              <div className="verification-text">
                <strong>Email Verification Required</strong>
                <p>Please verify your email address to access survey sharing features. Check your inbox for the verification link.</p>
              </div>
              <button 
                className="resend-verification-btn"
                onClick={handleResendVerification}
                data-testid="resend-verification-btn"
              >
                Resend Email
              </button>
            </div>
          </div>
        )}
        
        <h1 className="dashboard-title" data-testid="dashboard-title">Your Battlefield</h1>
        
        <div className="dashboard-actions">
          {subscriptionInfo && 
           (subscriptionInfo.subscription_status === 'free' || subscriptionInfo.subscription_status === 'recruit') && 
           subscriptionInfo.surveys_remaining === 0 ? (
            <UpgradeHotspot
              feature="Unlimited Surveys"
              description="You've used all 3 surveys on the Recruit plan. Upgrade to create unlimited surveys with no time limits!"
              currentTier={subscriptionInfo.subscription_status}
              requiredTier="sergeant"
              onUpgrade={() => setShowUpgradeModal(true)}
              position="inline"
            >
              <button 
                className="create-survey-btn locked" 
                onClick={() => setShowUpgradeModal(true)}
                data-testid="create-survey-button"
              >
                🔒 Create New Survey
              </button>
            </UpgradeHotspot>
          ) : (
            <button 
              className="create-survey-btn" 
              onClick={() => navigate('/builder')}
              data-testid="create-survey-button"
            >
              + Create New Survey
            </button>
          )}
          
          {/* AI Summary Report - Only for paid users */}
          {subscriptionInfo && (subscriptionInfo.subscription_status === 'sergeant' || 
            subscriptionInfo.subscription_status === 'lieutenant' || 
            subscriptionInfo.subscription_status === 'monthly' || 
            subscriptionInfo.subscription_status === 'yearly') ? (
            <button 
              className="ai-summary-btn" 
              onClick={handleGenerateSummary}
              disabled={surveys.length === 0}
              data-testid="ai-summary-button"
            >
              🤖 AI Summary Report
            </button>
          ) : (
            <UpgradeHotspot
              feature="AI Summary Reports"
              description="Get AI-powered analysis and insights from your survey responses. Identify trends, sentiment, and actionable recommendations!"
              currentTier={subscriptionInfo?.subscription_status || 'recruit'}
              requiredTier="sergeant"
              onUpgrade={() => setShowUpgradeModal(true)}
              position="inline"
            >
              <button 
                className="ai-summary-btn locked" 
                onClick={() => setShowUpgradeModal(true)}
                data-testid="ai-summary-button"
              >
                🔒 AI Summary Report
              </button>
            </UpgradeHotspot>
          )}
          
          {/* Import Template Button */}
          <button 
            className="import-template-btn" 
            onClick={handleImportTemplate}
            data-testid="import-template-button"
          >
            📥 Import Template
          </button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".json"
            onChange={processImportedTemplate}
            style={{ display: 'none' }}
          />
        </div>
        
        {surveys.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <p>No surveys yet. Create your first one!</p>
          </div>
        ) : (
          <div className="surveys-grid" data-testid="surveys-grid">
            {surveys.map((survey) => (
              <div key={survey.id} className="survey-card" data-testid={`survey-card-${survey.id}`}>
                <div className="survey-card-header">
                  <h3 className="survey-card-title" data-testid="survey-title">{survey.title}</h3>
                  <div className="survey-status-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={survey.is_active}
                        onChange={() => handleToggleActive(survey.id, survey.is_active)}
                        data-testid={`toggle-active-${survey.id}`}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className={`status-label ${survey.is_active ? 'active' : 'inactive'}`}>
                      {survey.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <p className="survey-card-description" data-testid="survey-description">
                  {survey.description || 'No description'}
                </p>
                <div className="survey-card-meta">
                  <span>{survey.questions.length} questions</span>
                  <span className="response-count" data-testid="response-count">
                    {survey.response_count || 0} responses
                  </span>
                </div>
                <div className="survey-card-actions">
                  {/* Per-survey AI Summary - Only for paid users */}
                  {subscriptionInfo && (subscriptionInfo.subscription_status === 'sergeant' || 
                    subscriptionInfo.subscription_status === 'lieutenant' || 
                    subscriptionInfo.subscription_status === 'monthly' || 
                    subscriptionInfo.subscription_status === 'yearly') ? (
                    <button 
                      className="action-btn action-btn-ai" 
                      onClick={() => handleGenerateSurveySummary(survey.id, survey.title)}
                      data-testid={`ai-summary-${survey.id}`}
                    >
                      🤖 AI Summary
                    </button>
                  ) : (
                    <button 
                      className="action-btn action-btn-ai locked" 
                      onClick={() => setShowUpgradeModal(true)}
                      data-testid={`ai-summary-${survey.id}`}
                    >
                      🔒 AI Summary
                    </button>
                  )}
                  <button 
                    className="action-btn action-btn-primary" 
                    onClick={() => navigate(`/analytics/${survey.id}`)}
                    data-testid="view-results-button"
                  >
                    View Results
                  </button>
                  <button 
                    className="action-btn action-btn-success" 
                    onClick={() => handleExportSurvey(survey.id, survey.title)}
                    data-testid={`export-survey-${survey.id}`}
                  >
                    📊 Export CSV
                  </button>
                  <button 
                    className={`action-btn action-btn-primary ${user && !user.email_verified && user.auth_type === 'email' ? 'disabled' : ''}`}
                    onClick={() => {
                      if (user && !user.email_verified && user.auth_type === 'email') {
                        toast.error('Please verify your email address to share survey links');
                        return;
                      }
                      handleEmbedLink(survey.id);
                    }}
                    disabled={user && !user.email_verified && user.auth_type === 'email'}
                    title={user && !user.email_verified && user.auth_type === 'email' ? 'Verify your email to access this feature' : 'Get survey link'}
                    data-testid="embed-link-button"
                  >
                    🔗 Embed Link {user && !user.email_verified && user.auth_type === 'email' && '🔒'}
                  </button>
                  <button 
                    className="action-btn action-btn-secondary" 
                    onClick={() => handleDuplicate(survey)}
                    data-testid="duplicate-button"
                  >
                    Duplicate
                  </button>
                  <button 
                    className="action-btn action-btn-info" 
                    onClick={() => handleShowQR(survey)}
                    data-testid="qr-code-button"
                  >
                    📱 QR Code
                  </button>
                  <button 
                    className="action-btn action-btn-secondary" 
                    onClick={() => exportTemplate(survey)}
                    data-testid="export-template-button"
                  >
                    📤 Export Template
                  </button>
                  <button 
                    className="action-btn action-btn-danger" 
                    onClick={() => handleDelete(survey.id)}
                    data-testid="delete-button"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Summary Modal */}
      {showSummaryModal && (
        <div className="modal-overlay" onClick={() => setShowSummaryModal(false)} data-testid="summary-modal">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {summaryType === 'all' ? 'AI Summary Report - All Surveys' : 
                 summaryReport ? `AI Summary - ${summaryReport.survey_title}` : 'AI Summary Report'}
              </h2>
              <button className="modal-close" onClick={() => setShowSummaryModal(false)} data-testid="close-modal">
                ✕
              </button>
            </div>
            
            {generatingSummary ? (
              <div className="modal-loading">
                <div className="loading-spinner">Generating AI Summary...</div>
              </div>
            ) : summaryReport ? (
              <div className="modal-body">
                <div className="summary-section">
                  <div className="summary-stats">
                    {summaryType === 'all' && summaryReport.total_surveys && (
                      <div className="stat-item">
                        <span className="stat-number">{summaryReport.total_surveys}</span>
                        <span className="stat-label">Total Surveys</span>
                      </div>
                    )}
                    <div className="stat-item">
                      <span className="stat-number">{summaryReport.total_responses}</span>
                      <span className="stat-label">Total Responses</span>
                    </div>
                  </div>
                </div>

                <div className="summary-section">
                  <h3>Executive Summary</h3>
                  <p className="summary-text">{summaryReport.executive_summary}</p>
                </div>

                <div className="summary-section">
                  <h3>Overall Sentiment</h3>
                  <p className="sentiment-text">{summaryReport.overall_sentiment}</p>
                </div>

                <div className="summary-section">
                  <h3>Key Insights</h3>
                  <ul className="insights-list">
                    {summaryReport.key_insights.map((insight, index) => (
                      <li key={index}>{insight}</li>
                    ))}
                  </ul>
                </div>

                <div className="summary-section">
                  <h3>Recommendations</h3>
                  <ul className="recommendations-list">
                    {summaryReport.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>

                <div className="summary-footer">
                  <div className="summary-footer-info">
                    <small>Generated: {new Date(summaryReport.generated_at).toLocaleString()}</small>
                  </div>
                  <div className="summary-download-actions">
                    <button 
                      className="action-btn action-btn-primary"
                      onClick={downloadSummaryAsText}
                      title="Download as plain text file"
                    >
                      📄 Download TXT
                    </button>
                    <button 
                      className="action-btn action-btn-secondary"
                      onClick={printSummary}
                      title="Print or save as PDF"
                    >
                      🖨️ Print/PDF
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
            <div className="upgrade-modal-header">
              <h2>Choose Your Command Tier</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowUpgradeModal(false)}
                data-testid="close-upgrade-modal"
              >
                ✕
              </button>
            </div>
            
            <div className="upgrade-modal-content">
              {subscriptionInfo && 
               (subscriptionInfo.subscription_status === 'recruit' || subscriptionInfo.subscription_status === 'free') && 
               subscriptionInfo.surveys_remaining === 0 ? (
                <div className="limit-reached-notice">
                  <p className="upgrade-message">
                    <strong>⚠️ Survey Limit Reached!</strong>
                  </p>
                  <p className="upgrade-message">
                    You've used all 3 surveys on the Recruit plan. Delete an existing survey to create a new one, or upgrade to Sergeant/Lieutenant for unlimited surveys!
                  </p>
                </div>
              ) : (
                <p className="upgrade-message">
                  Select the plan that fits your reconnaissance mission. All plans include AI-powered features!
                </p>
              )}
              
              <div className="pricing-cards three-tier">
                <div className="pricing-card">
                  <div className="plan-badge">FREE</div>
                  <h3>🎖️ Recruit</h3>
                  <div className="price">
                    <span className="amount">$0</span>
                    <span className="period">/forever</span>
                  </div>
                  <ul className="features">
                    <li>✓ 3 Active Surveys</li>
                    <li>✓ Up to 10 Questions per Survey</li>
                    <li>✓ 7-Day Survey Duration</li>
                    <li>✓ AI Survey Generation</li>
                    <li>✓ AI Summary Reports</li>
                    <li>✓ CSV Export</li>
                    <li>✓ Basic Analytics</li>
                  </ul>
                  {subscriptionInfo && 
                   (subscriptionInfo.subscription_status === 'recruit' || subscriptionInfo.subscription_status === 'free') && 
                   subscriptionInfo.surveys_remaining === 0 ? (
                    <button 
                      className="upgrade-btn secondary"
                      onClick={() => setShowUpgradeModal(false)}
                      data-testid="upgrade-recruit-button"
                    >
                      Stay on Recruit (Delete a Survey)
                    </button>
                  ) : (
                    <button 
                      className="upgrade-btn"
                      onClick={() => setShowUpgradeModal(false)}
                      data-testid="upgrade-recruit-button"
                    >
                      Current Plan
                    </button>
                  )}
                </div>
                
                <div className="pricing-card">
                  <div className="plan-badge">MONTHLY</div>
                  <h3>⭐ Sergeant</h3>
                  <div className="price">
                    <span className="amount">$10</span>
                    <span className="period">/month</span>
                  </div>
                  <ul className="features">
                    <li>✓ Unlimited Surveys</li>
                    <li>✓ Up to 50 Questions per Survey</li>
                    <li>✓ No Time Limits</li>
                    <li>✓ AI Survey Generation</li>
                    <li>✓ AI Summary Reports</li>
                    <li>✓ CSV Export</li>
                    <li>✓ Advanced Analytics</li>
                    <li>✓ Priority Support</li>
                  </ul>
                  <button 
                    className="upgrade-btn"
                    onClick={() => navigate('/manage-subscription')}
                    data-testid="upgrade-sergeant-button"
                  >
                    Subscribe Monthly
                  </button>
                </div>
                
                <div className="pricing-card featured">
                  <div className="best-value-badge">MOST POPULAR - 40% OFF</div>
                  <h3>🏅 Lieutenant</h3>
                  <div className="price">
                    <span className="original-price">$95</span>
                    <span className="amount">$57</span>
                    <span className="period">/year</span>
                  </div>
                  <p className="savings">Save $63/year + Early Adopter Bonus!</p>
                  <ul className="features">
                    <li>✓ Unlimited Surveys</li>
                    <li>✓ Up to 50 Questions per Survey</li>
                    <li>✓ No Time Limits</li>
                    <li>✓ AI Survey Generation</li>
                    <li>✓ AI Summary Reports</li>
                    <li>✓ CSV Export</li>
                    <li>✓ Advanced Analytics</li>
                    <li>✓ Priority Support</li>
                    <li>✓ Annual Billing Discount</li>
                  </ul>
                  <button 
                    className="upgrade-btn featured"
                    onClick={() => navigate('/manage-subscription')}
                    data-testid="upgrade-lieutenant-button"
                  >
                    Subscribe Yearly
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Survey Selector Modal */}
      {showSurveySelector && (
        <div className="modal-overlay" onClick={() => setShowSurveySelector(false)}>
          <div className="modal-content survey-selector-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 Select Surveys for AI Summary</h2>
              <button className="modal-close" onClick={() => setShowSurveySelector(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="selector-description">
                Choose which surveys to include in your AI summary report.
              </p>
              
              <div className="selector-actions">
                <button className="action-btn action-btn-primary" onClick={selectAllSurveys}>
                  Select All ({surveys.length})
                </button>
                <button className="action-btn action-btn-secondary" onClick={deselectAllSurveys}>
                  Deselect All
                </button>
                <div className="selected-count">
                  {selectedSurveys.length} of {surveys.length} selected
                </div>
              </div>

              <div className="survey-selection-list">
                {surveys.map(survey => (
                  <label key={survey.id} className="survey-checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedSurveys.includes(survey.id)}
                      onChange={() => toggleSurveySelection(survey.id)}
                    />
                    <div className="survey-checkbox-content">
                      <div className="survey-checkbox-title">{survey.title}</div>
                      <div className="survey-checkbox-meta">
                        {survey.questions.length} questions • {survey.response_count || 0} responses
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="selector-modal-actions">
                <button 
                  className="action-btn action-btn-success"
                  onClick={generateSelectedSummary}
                  disabled={selectedSurveys.length === 0}
                >
                  🤖 Generate AI Summary ({selectedSurveys.length} surveys)
                </button>
                <button className="action-btn action-btn-secondary" onClick={() => setShowSurveySelector(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Embed Link Modal */}
      {showEmbedModal && (
        <div className="modal-overlay" onClick={() => setShowEmbedModal(false)}>
          <div className="modal-content embed-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔗 Embed Survey Link</h2>
              <button className="modal-close" onClick={() => setShowEmbedModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="embed-description">
                Create a custom embedded link for your survey.
              </p>
              
              <div className="embed-input-section">
                <label htmlFor="embed-text">Link Text:</label>
                <input
                  id="embed-text"
                  type="text"
                  value={embedText}
                  onChange={(e) => setEmbedText(e.target.value)}
                  placeholder="How did we do?"
                  className="embed-text-input"
                />
              </div>

              <div className="embed-preview-section">
                <label>Preview:</label>
                <div className="embed-preview">
                  <a 
                    href={`${window.location.origin}/survey/${embedSurveyId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {embedText || 'How did we do?'}
                  </a>
                </div>
              </div>

              <div className="embed-code-section">
                <label>HTML Code:</label>
                <div className="embed-code-box">
                  <code>
                    {`<a href="${window.location.origin}/survey/${embedSurveyId}">${embedText || 'How did we do?'}</a>`}
                  </code>
                </div>
              </div>

              <div className="embed-options-info">
                <p><strong>Choose how to copy:</strong></p>
                <ul>
                  <li><strong>Copy for Email:</strong> Paste directly into Gmail, Outlook as a clickable link</li>
                  <li><strong>Copy HTML Code:</strong> Use in websites or HTML emails</li>
                  <li><strong>Copy Plain Link:</strong> Copy the raw survey URL</li>
                </ul>
              </div>

              <div className="embed-modal-actions">
                <button className="action-btn action-btn-success" onClick={copyForEmail}>
                  ✉️ Copy for Email
                </button>
                <button className="action-btn action-btn-primary" onClick={copyEmbedCode}>
                  📋 Copy HTML Code
                </button>
                <button className="action-btn action-btn-info" onClick={copyPlainLink}>
                  🔗 Copy Plain Link
                </button>
                <button className="action-btn action-btn-secondary" onClick={() => setShowEmbedModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && qrSurvey && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📱 QR Code</h2>
              <button className="modal-close" onClick={() => setShowQRModal(false)}>✕</button>
            </div>
            <div className="modal-body qr-modal-body">
              <p className="qr-survey-title">{qrSurvey.title}</p>
              <div className="qr-code-container" ref={qrRef}>
                <QRCodeSVG 
                  value={`${BACKEND_URL}/survey/${qrSurvey.id}`}
                  size={250}
                  level="H"
                  includeMargin={true}
                  bgColor="#ffffff"
                  fgColor="#00897b"
                />
              </div>
              <p className="qr-url">{`${BACKEND_URL}/survey/${qrSurvey.id}`}</p>
              <div className="qr-actions">
                <button className="qr-download-btn" onClick={downloadQRCode} data-testid="download-qr">
                  📥 Download QR Code
                </button>
                <button className="qr-copy-btn" onClick={() => {
                  navigator.clipboard.writeText(`${BACKEND_URL}/survey/${qrSurvey.id}`);
                  toast.success('Survey URL copied!');
                }}>
                  📋 Copy URL
                </button>
              </div>
              <p className="qr-tip">💡 Print this QR code for in-store displays, receipts, or marketing materials</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
